<script>
import { mapState, mapActions } from 'vuex'

export default {
   data() {
      return {
         fields: [
            { key: 'rank', label: '#', sortable: false },
            { key: 'kbli', label: 'Kode KBLI', sortable: true },
            { key: 'nama', label: 'Nama KBLI', sortable: true },
            { key: 'total', label: 'Total', sortable: true },
         ],
         loading: false,
      }
   },
   computed: {
      ...mapState({
         topKbli: state => state.DashboardLkpm.umkKbli,
         tahun: state => state.DashboardLkpm.tahun,
         refreshTimestamp: state => state.DashboardLkpm.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      rankedData() {
         return (this.topKbli.data || [])
            .slice()
            .sort((a, b) => b.total - a.total)
            .slice(0, 10)
            .map((item, index) => ({
               ...item,
               rank: index + 1,
            }))
      },
      maxTotal() {
         if (!this.rankedData.length) return 100
         return Math.max(...this.rankedData.map(item => item.total))
      },
   },
   watch: {
      tahun(newYear) {
         this.loadData(newYear)
      },
      refreshTimestamp() {
         this.loadData(this.tahun)
      },
   },
   mounted() {
      this.loadData(this.tahun)
   },
   methods: {
      ...mapActions({
         getUmkKbli: 'DashboardLkpm/getUmkKbli',
      }),
      async loadData(year) {
         this.loading = true
         try {
            await this.getUmkKbli(year)
         } catch (error) {
            // Tabel tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
      getProgressWidth(total) {
         if (this.maxTotal === 0) return '0%'
         return `${(total / this.maxTotal) * 100}%`
      },
      getProgressColor(index) {
         const colors = [
            '#9f1e15',
            '#28c76f',
            '#7367f0',
            '#ff9f43',
            '#00cfe8',
            '#ea5455',
            '#1d4ed8',
            '#8b5cf6',
            '#f59e0b',
            '#10b981',
         ]
         return colors[index % colors.length]
      },
   },
}
</script>

<template>
   <b-row>
      <b-col cols="12">
         <b-overlay
            :show="loading"
            :variant="isDark ? 'dark' : 'white'"
            spinner-variant="primary"
            blur="0"
            opacity=".75"
            rounded="sm"
         >
            <b-card
               class="card-wrapper"
               :class="{ 'card-wrapper-dark': isDark }"
            >
               <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                  <div class="mb-1 mb-md-0">
                     <h4 class="mb-25">
                        Top 10 KBLI UMK Tahun {{ tahun }}
                     </h4>
                     <small class="text-muted">Total Realisasi UMK tertinggi berdasarkan Kode Klasifikasi Baku Lapangan Usaha Indonesia</small>
                  </div>
               </div>

               <!-- Desktop View - Table with Progress Bar -->
               <div class="d-none d-md-block">
                  <b-table
                     :items="rankedData"
                     :fields="fields"
                     :busy="false"
                     class="kbli-table"
                     responsive
                  >
                     <template #cell(rank)="data">
                        <div
                           class="rank-badge d-flex align-items-center justify-content-center font-weight-bolder"
                           :style="{
                              background: getProgressColor(data.index) + '20',
                              color: getProgressColor(data.index),
                           }"
                        >
                           {{ data.item.rank }}
                        </div>
                     </template>

                     <template #cell(kbli)="data">
                        <div class="font-weight-bolder">
                           {{ data.item.kbli }}
                        </div>
                     </template>

                     <template #cell(nama)="data">
                        <div class="text-wrap" style="max-width: 300px;">
                           {{ data.item.nama }}
                        </div>
                     </template>

                     <template #cell(total)="data">
                        <div class="d-flex align-items-center">
                           <div class="progress-wrapper mr-1">
                              <div
                                 class="progress-bar-custom"
                                 :style="{
                                    width: getProgressWidth(data.item.total),
                                    background: getProgressColor(data.index),
                                 }"
                              />
                           </div>
                           <span class="font-weight-bolder">
                              {{ data.item.total.toLocaleString('id-ID') }}
                           </span>
                        </div>
                     </template>
                  </b-table>
               </div>

               <!-- Mobile View - Card List -->
               <div class="d-md-none">
                  <b-list-group>
                     <b-list-group-item
                        v-for="(item, index) in rankedData"
                        :key="item.kbli"
                        class="kbli-card-item"
                     >
                        <div class="d-flex justify-content-between align-items-start mb-1">
                           <div class="d-flex align-items-center">
                              <div
                                 class="rank-badge-sm mr-2 font-weight-bolder"
                                 :style="{
                                    background: getProgressColor(index) + '20',
                                    color: getProgressColor(index),
                                 }"
                              >
                                 {{ item.rank }}
                              </div>
                              <div>
                                 <div class="font-weight-bolder">
                                    {{ item.kbli }}
                                 </div>
                                 <small class="text-muted">{{ item.nama }}</small>
                              </div>
                           </div>
                           <div
                              class="font-weight-bolder ml-2"
                              :style="{ color: getProgressColor(index) }"
                           >
                              {{ item.total.toLocaleString('id-ID') }}
                           </div>
                        </div>
                        <div class="progress-bar-custom" :style="{ width: getProgressWidth(item.total), background: getProgressColor(index) }" />
                     </b-list-group-item>
                  </b-list-group>
               </div>
            </b-card>
         </b-overlay>
      </b-col>
   </b-row>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
   background-color: #ffffff;
}

.card-wrapper-dark {
   background-color: #283046;
   box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}

.card-wrapper-dark .kbli-card-item {
   border-bottom-color: #3b4253;
   background: transparent;
}

.rank-badge {
   width: 32px;
   height: 32px;
   border-radius: 8px;
   font-size: 0.9rem;
}

.rank-badge-sm {
   width: 28px;
   height: 28px;
   border-radius: 6px;
   font-size: 0.85rem;
   display: flex;
   align-items: center;
   justify-content: center;
}

.progress-wrapper {
   width: 100px;
   height: 8px;
   background: #e9ecef;
   border-radius: 4px;
   overflow: hidden;
   flex-shrink: 0;
}

.progress-bar-custom {
   height: 100%;
   border-radius: 4px;
   transition: width 0.3s ease;
}

.kbli-card-item {
   border: 0;
   border-bottom: 1px solid #f1f1f2;
   padding: 0.75rem 0;
   background: transparent;
}

.kbli-card-item:last-child {
   border-bottom: 0;
}

.kbli-table :deep(.table) {
   margin-bottom: 0;
}

.kbli-table :deep(th) {
   border-top: none;
   font-size: 0.85rem;
   color: #6e6b7b;
}

.kbli-table :deep(td) {
   vertical-align: middle;
}
</style>
